/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useBeautifulDayMonsterService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 15 Nov 2021 2:13:28 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { BuyerModel } from 'models/BuyerModel';
import { CustomerNameModel } from 'models/CustomerNameModel';
import { PaymentTermsModel } from 'models/PaymentTermsModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetCustomersParams {
  dataAreaId: string | null;
  customerNameContains?: string;
  customerAccountContains?: string;
  orderByField?: string;
  overrideSkipTake?: boolean;

}

export interface GetCustomersResponseModel {
  data: CustomerNameModel[];
  totalCount: number;
}

export interface GetBuyersParams {
  dataAreaId: string | null;
  buyerNameContains?: string;
  offset: number;
  customerId: string;
}

export interface GetBuyersResponseModel {
  data: BuyerModel[];
  totalCount: number;
}
export interface CreateBuyerParams {
  buyerData: {
    dataAreaId: string;
    firstName: string;
    lastName: string;
  };
  customerId: string;
}

export interface CreateBuyerResponseModel {
  buyerId: string;
  buyerName: string;
}
export interface GetPaymentTermsParams {
  dataAreaId: string | null;
  paymentTermContains?: string;
  offset: number;
}

export interface GetPaymentTermsResponseModel {
  data: PaymentTermsModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const beautifulDayMonsterBaseUrl = process.env.REACT_APP_BEAUTIFUL_DAY_MONSTER_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_BEAUTIFUL_DAY_MONSTER;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: beautifulDayMonsterBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: rawBaseQuery,
  endpoints: (builder) => ({
    getCustomers: builder.query<GetCustomersResponseModel, GetCustomersParams>({
  
  query: ({ dataAreaId, customerNameContains, customerAccountContains, orderByField,overrideSkipTake  }) => ({
        url: `customers`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          customerNameContains: customerNameContains ? encodeValue({ isUrl: false, value: customerNameContains }) : undefined,
          customerAccountContains: customerAccountContains ? encodeValue({ isUrl: false, value: customerAccountContains }) : undefined,
          orderByField,
          overrideSkipTake
        }
      }),
      // onQueryStarted: async (_arg, _api) => {
      //   try {
      //     await _api.queryFulfilled;
      //     const { originalArgs } = _api.getCacheEntry();
      //     const { queries } = _api.getState().customerApi;
      //     const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
      //     const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

      //     if (lastQueryData) {
      //       _api.dispatch(
      //         customerApi.util.updateQueryData('getCustomers', _arg, (draft) => {
      //           draft.data = [...(lastQueryData.data as GetCustomersResponseModel | WritableDraft<GetCustomersResponseModel>).data, ...draft.data];
      //         })
      //       );
      //     }
      //   } catch (error) {
      //     console.log(error);
      //   }
      // }
    }),
    getPaymentTerms: builder.query<GetPaymentTermsResponseModel, GetPaymentTermsParams>({
      query: ({ dataAreaId, paymentTermContains, offset }) => ({
        url: `paymentTerms`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          paymentTermContains: paymentTermContains ? encodeValue({ isUrl: false, value: paymentTermContains }) : undefined,
          offset
        }
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: 'diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const buyerApi = createApi({
  reducerPath: 'buyerApi',
  baseQuery: rawBaseQuery,
  tagTypes: ['Buyer'],
  endpoints: (builder) => ({
    getBuyers: builder.query<GetBuyersResponseModel, GetBuyersParams>({
      query: ({ dataAreaId, offset, customerId }) => ({
        url: `customers/${customerId}/buyers`,
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', offset }
      }),
      providesTags: ['Buyer'],
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().buyerApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getBuyersByName: builder.query<GetBuyersResponseModel, GetBuyersParams>({
      query: ({ dataAreaId, buyerNameContains, offset }) => ({
        url: 'buyers',
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          buyerNameContains: buyerNameContains ? encodeValue({ isUrl: false, value: buyerNameContains }) : undefined,
          offset
        }
      }),
      providesTags: ['Buyer']
    }),
    createBuyer: builder.mutation<CreateBuyerResponseModel, CreateBuyerParams>({
      query: ({ buyerData, customerId }) => ({
        url: `customers/${customerId}/buyers`,
        method: 'POST',
        body: buyerData
      }),
      invalidatesTags: ['Buyer']
    })
  })
});

export const { useGetCustomersQuery, useGetPaymentTermsQuery, useGetVersionQuery, useGetApiNameQuery, useLazyGetCustomersQuery } = customerApi;
export const { useGetBuyersQuery, useCreateBuyerMutation } = buyerApi;
